/**
 * agenticStore — Zustand store cho agentic chat results.
 *
 * Lưu trữ kết quả của các agentic tasks theo session_id.
 * Component subscribe vào đúng session_id của mình để nhận update.
 *
 * Design:
 * - Keyed by session_id để isolation hoàn toàn giữa các session.
 * - Capped tại MAX_RESULTS_PER_SESSION messages để tránh memory leak.
 * - progress events accumulate; result event đánh dấu task done.
 */

import { create } from "zustand";
import type { AgenticStreamEvent } from "@/shared/hooks/useAgenticStream";

const MAX_RESULTS_PER_SESSION = 200;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface AgenticTaskState {
  task_id: string;
  session_id: string;
  status: "running" | "done" | "error";
  /** Final answer — populated on "result" event */
  result?: string;
  /** Error message — populated on "error" event */
  error?: string;
  /** Streaming step outputs — populated on "progress" events */
  steps: Array<{
    step: number;
    total_steps: number;
    agent_type: string;
    content: string;
  }>;
  intent?: string;
  tokens_used?: number;
  started_at: string;
  completed_at?: string;
}

interface AgenticState {
  /** Tasks keyed by session_id → task_id → AgenticTaskState */
  tasks: Record<string, Record<string, AgenticTaskState>>;

  /** Handle an incoming SSE event — dispatched by useAgenticStream */
  handleEvent: (event: AgenticStreamEvent) => void;

  /** Get all tasks for a session */
  getSessionTasks: (sessionId: string) => AgenticTaskState[];

  /** Get a specific task */
  getTask: (sessionId: string, taskId: string) => AgenticTaskState | undefined;

  /** Remove completed/errored tasks older than maxAgeMs */
  pruneOldTasks: (maxAgeMs?: number) => void;

  /** Clear all tasks for a session (e.g., on session close) */
  clearSession: (sessionId: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Store
// ─────────────────────────────────────────────────────────────────────────────

export const useAgenticStore = create<AgenticState>((set, get) => ({
  tasks: {},

  handleEvent: (event: AgenticStreamEvent) => {
    const { session_id, task_id, type } = event;
    if (!session_id || !task_id) return; // connected/heartbeat events have no task_id

    set((state) => {
      const sessionTasks = state.tasks[session_id] ?? {};
      const existing = sessionTasks[task_id];

      let updated: AgenticTaskState;

      if (type === "progress") {
        updated = existing
          ? {
              ...existing,
              steps: [
                ...existing.steps,
                {
                  step: event.step ?? 0,
                  total_steps: event.total_steps ?? 0,
                  agent_type: event.agent_type ?? "unknown",
                  content: event.content ?? "",
                },
              ].slice(-MAX_RESULTS_PER_SESSION),
            }
          : {
              task_id,
              session_id,
              status: "running",
              steps: [
                {
                  step: event.step ?? 1,
                  total_steps: event.total_steps ?? 1,
                  agent_type: event.agent_type ?? "unknown",
                  content: event.content ?? "",
                },
              ],
              intent: event.intent,
              started_at: event.timestamp,
            };
      } else if (type === "result") {
        updated = {
          ...(existing ?? {
            task_id,
            session_id,
            steps: [],
            started_at: event.timestamp,
          }),
          status: "done",
          result: event.content,
          intent: event.intent ?? existing?.intent,
          tokens_used: event.tokens_used,
          completed_at: event.timestamp,
        };
      } else if (type === "error") {
        updated = {
          ...(existing ?? {
            task_id,
            session_id,
            steps: [],
            started_at: event.timestamp,
          }),
          status: "error",
          error: event.error ?? "Unknown error",
          completed_at: event.timestamp,
        };
      } else {
        return state; // connected/heartbeat — no task state change
      }

      return {
        tasks: {
          ...state.tasks,
          [session_id]: {
            ...sessionTasks,
            [task_id]: updated,
          },
        },
      };
    });
  },

  getSessionTasks: (sessionId) => {
    const sessionTasks = get().tasks[sessionId] ?? {};
    return Object.values(sessionTasks);
  },

  getTask: (sessionId, taskId) => get().tasks[sessionId]?.[taskId],

  pruneOldTasks: (maxAgeMs = 30 * 60 * 1000) => {
    const cutoff = Date.now() - maxAgeMs;
    set((state) => {
      const newTasks: typeof state.tasks = {};
      for (const [sessionId, sessionTasks] of Object.entries(state.tasks)) {
        const filtered: Record<string, AgenticTaskState> = {};
        for (const [taskId, task] of Object.entries(sessionTasks)) {
          const age = task.completed_at
            ? new Date(task.completed_at).getTime()
            : Date.now();
          if (task.status === "running" || age > cutoff) {
            filtered[taskId] = task;
          }
        }
        if (Object.keys(filtered).length > 0) {
          newTasks[sessionId] = filtered;
        }
      }
      return { tasks: newTasks };
    });
  },

  clearSession: (sessionId) => {
    set((state) => {
      const { [sessionId]: _, ...rest } = state.tasks;
      return { tasks: rest };
    });
  },
}));
